/**
 * Document detail page.
 * Shows document content and allows editing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDocument, useDocumentView, useUpdateDocumentName, useUpdateDocumentContent, useDeleteDocument, useProcessDocument, useDeleteTranscription, useMarkDocumentReady } from "../hooks/useDocuments";
import { useProject } from "../hooks/useProjects";
import { ProductExtractionStatus } from "../components/catalog/ProductExtractionStatus";
import { useWorkspaceUrl } from "../lib/routes";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { PageHeader } from "../components/layout/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { KiiBadge } from "@/components/kii/kii-badge";
import AwaitingBoostsBadge from "../components/AwaitingBoostsBadge";
import BoostGateError from "../components/BoostGateError";
import { InsufficientBoostsError } from "../services/naraDoor";
import { KiiConfirmDialog } from "@/components/kii/kii-confirm-dialog";
import { DocumentHistory } from "../components/DocumentHistory";
import { GeneratedReportLinks } from "../components/GeneratedReportLinks";
import {
  CheckCircle,
  Eraser,
  FileText,
  Trash2,
  Edit,
  Edit2,
  Save,
  X,
  Play,
  Square,
  Clock,
  RefreshCw,
  Download,
  MoreVertical,
  FileDown,
  Paperclip,
  Info,
  MapPin,
  Compass,
  Pencil,
} from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { KiiNaraImage } from "@/components/kii/kii-nara-image";
import { memo } from "react";

// Stable references for react-markdown to prevent re-render loops
const remarkPlugins = [remarkGfm];

function naraUrlTransform(url: string) {
  if (url.startsWith("nara-file://")) return url;
  const colon = url.indexOf(":");
  const slash = url.indexOf("/");
  if (colon === -1 || (slash !== -1 && colon > slash) || /^(https?|mailto)$/i.test(url.slice(0, colon))) return url;
  return "";
}

interface ImageFileRef {
  id: string;
  url?: string;
  filename?: string;
}

function resolveNaraFileUrl(src: string, imageFiles?: ImageFileRef[]): string | undefined {
  if (!src.startsWith("nara-file://")) return src;
  const fileId = src.replace("nara-file://", "");
  const file = imageFiles?.find((f) => f.id === fileId);
  return file?.url?.startsWith("http") ? file.url : undefined;
}

function MarkdownWithImages({ content, imageFiles }: { content: string; imageFiles?: ImageFileRef[] }) {
  // Pre-resolve all nara-file:// URLs to signed URLs in the markdown string
  const resolvedContent = content.replace(
    /!\[([^\]]*)\]\(nara-file:\/\/([^)]+)\)/g,
    (_match, alt, fileId) => {
      const file = imageFiles?.find((f) => f.id === fileId);
      const url = file?.url?.startsWith("http") ? file.url : "";
      return url ? `![${alt}](${url})` : `![${alt}]()`;
    }
  );

  return (
    <Markdown remarkPlugins={remarkPlugins}>
      {resolvedContent}
    </Markdown>
  );
}

export function DocumentDetail() {
  const { t } = useTranslation();
  const { projectId, id: documentId } = useParams<{
    projectId: string;
    id: string;
  }>();
  const navigate = useNavigate();
  const urls = useWorkspaceUrl();
  const { isLoading: workspaceLoading } = useWorkspace();
  const { formatDate: formatDateHook } = useDateFormatter();

  // Hooks now get workspaceId automatically from the store
  const { data: document, isLoading: documentLoading } = useDocument(projectId, documentId);
  const { data: documentView, isLoading: viewLoading } = useDocumentView(projectId, documentId, document?.status);
  const { data: project } = useProject(projectId);
  const updateName = useUpdateDocumentName();
  const updateContent = useUpdateDocumentContent();
  const deleteDocument = useDeleteDocument();
  const processDocument = useProcessDocument();
  const deleteTranscriptionMutation = useDeleteTranscription();
  const markReady = useMarkDocumentReady();

  const audioRef = useRef<HTMLAudioElement>(null);
  const stopTimeRef = useRef<number | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [zoomedImage, setZoomedImage] = useState<{ url: string; title: string; timestamp?: string } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // The message of the last failed save, rename or delete; cleared when one succeeds.
  const [actionError, setActionError] = useState<string | null>(null);
  const [isTranscribeDialogOpen, setIsTranscribeDialogOpen] = useState(false);
  const [isDeleteTranscriptionDialogOpen, setIsDeleteTranscriptionDialogOpen] = useState(false);
  const [playingObsIndex, setPlayingObsIndex] = useState<number | null>(null);
  const [playbackTime, setPlaybackTime] = useState<number | null>(null);
  const [photoMetadata, setPhotoMetadata] = useState<Record<string, any> | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);

  const handleStartEdit = () => {
    const content = documentView?.content_json;
    setEditContent(
      typeof content === "string" ? content : JSON.stringify(content, null, 2) || ""
    );
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!projectId || !documentId) return;

    try {
      let contentJson;
      try {
        contentJson = JSON.parse(editContent);
      } catch {
        contentJson = editContent;
      }

      await updateContent.mutateAsync({
        projectId,
        documentId,
        contentJson,
      });
      setIsEditing(false);
      setActionError(null);
    } catch (error) {
      console.error("Failed to save document:", error);
      setActionError(t("documentDetail.saveError"));
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent("");
  };

  const handleDelete = async () => {
    if (!projectId || !documentId) return;
    try {
      await deleteDocument.mutateAsync({ projectId, documentId });
      if (urls) {
        navigate(urls.project(projectId));
      }
    } catch (error) {
      console.error("Failed to delete document:", error);
      setActionError(t("documentDetail.deleteError"));
    }
  };

  const handleProcess = async () => {
    if (!documentId || !projectId) return;
    try {
      await processDocument.mutateAsync({ documentId, projectId });
    } catch (error) {
      console.error("Failed to process document:", error);
    }
  };

  const handleDeleteTranscription = async () => {
    if (!documentId || !projectId) return;
    try {
      await deleteTranscriptionMutation.mutateAsync({ documentId, projectId });
    } catch (error) {
      console.error("Failed to delete transcription:", error);
      setActionError(t("documentDetail.deleteTranscriptionError"));
    } finally {
      setIsDeleteTranscriptionDialogOpen(false);
    }
  };


  const isStuck = (): boolean => {
    if (document?.status !== "processing") return false;
    if (!document?.updated_at) return false;
    const updatedAt = new Date(document.updated_at).getTime();
    return Date.now() - updatedAt > 30 * 60 * 1000;
  };

  const getStatusBadge = (status?: string) => {
    if (status === "failed") {
      return (
        <KiiBadge variant="destructive">
          {t("documentDetail.statusFailed", "Failed")}
        </KiiBadge>
      );
    }
    if (status === "processing" && isStuck()) {
      return (
        <KiiBadge variant="warning">
          {t("documentDetail.statusStuck", "Stuck — retry?")}
        </KiiBadge>
      );
    }
    if (status === "processing") {
      return (
        <KiiBadge variant="warning" className="gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-yellow-900 animate-pulse" />
          Processing...
        </KiiBadge>
      );
    }
    if (status === "awaiting_boosts" || status === "insufficient_boosts") {
      return <AwaitingBoostsBadge />;
    }
    const variantMap: Record<string, "success" | "destructive" | "secondary"> = {
      ready: "success",
      completed: "success",
      failed: "destructive",
      pending: "secondary",
    };
    return (
      <KiiBadge variant={variantMap[status || "pending"] || "secondary"}>
        {status || "pending"}
      </KiiBadge>
    );
  };

  // Check if this is a markdown document by type
  const isMarkdownDocument = () => {
    return document?.document_type === "markdown";
  };

  // Check if document has observations format (editable via transcript editor)
  const hasObservationsFormat = () => {
    const content = documentView?.content_json;
    return (
      typeof content === "object" &&
      content !== null &&
      "observations" in content &&
      Array.isArray((content as { observations: unknown[] }).observations)
    );
  };

  // Compute effective status - prefer documentView.status (computed from files)
  const getEffectiveStatus = () => {
    // documentView.status is computed from file statuses (most accurate)
    if (documentView?.status) {
      return documentView.status;
    }
    // Fall back to document status
    return document?.status;
  };

  const handleEditTranscript = () => {
    // Navigate to the transcript editor
    if (urls && projectId && documentId) {
      navigate(urls.documentTranscriptEdit(projectId, documentId));
    }
  };

  // Helper to get photo URL by ID from image_files
  const getPhotoUrl = (photoId: string): string | null => {
    const imageFiles = documentView?.image_files || [];
    const photo = imageFiles.find((f: any) => f.id === photoId);
    return photo?.url || null;
  };

  // Helper to get photo EXIF timestamp by ID from image_files
  const getPhotoTimestamp = (photoId: string): string | undefined => {
    const imageFiles = documentView?.image_files || [];
    const photo = imageFiles.find((f: any) => f.id === photoId);
    const meta = photo?.metadata as Record<string, any> | undefined;
    return meta?.nara?.created_at as string | undefined;
  };

  // Helper to get full photo metadata by ID
  const getPhotoMetadata = (photoId: string): Record<string, any> | null => {
    const imageFiles = documentView?.image_files || [];
    const photo = imageFiles.find((f: any) => f.id === photoId);
    return (photo?.metadata as Record<string, any>) || null;
  };

  const formatTimestamp = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const stopObsPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    stopTimeRef.current = null;
    setPlayingObsIndex(null);
    setPlaybackTime(null);
  }, []);

  const playObservation = useCallback((index: number, startTime: number, endTime: number | null) => {
    const audio = audioRef.current;
    if (!audio) return;

    // If same observation is playing, stop it
    if (playingObsIndex === index) {
      stopObsPlayback();
      return;
    }

    stopTimeRef.current = endTime !== null ? endTime + 3 : null;
    setPlayingObsIndex(index);
    setPlaybackTime(startTime);
    audio.currentTime = Math.max(0, startTime - 3);
    audio.play();
  }, [playingObsIndex, stopObsPlayback]);

  // Monitor audio timeupdate to advance displayed time and auto-stop at boundary
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (playingObsIndex === null) return;
      const current = audio.currentTime;
      setPlaybackTime(current);

      if (stopTimeRef.current !== null && current >= stopTimeRef.current) {
        audio.pause();
        stopTimeRef.current = null;
        setPlayingObsIndex(null);
        setPlaybackTime(null);
      }
    };

    const onEnded = () => {
      stopTimeRef.current = null;
      setPlayingObsIndex(null);
      setPlaybackTime(null);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
  }, [playingObsIndex]);

  const renderContent = () => {
    const content = documentView?.content_json;

    // Handle NaraMachine observations format (from /document/transcript endpoint)
    if (
      typeof content === "object" &&
      content !== null &&
      "observations" in content
    ) {
      const observations = (content as { observations: Array<{ title: string; description: string; photo_ids?: string[]; start_time?: number; end_time?: number; raw_words?: Array<{ word: string; start_sec: number; end_sec: number }>; photo_timestamps?: Record<string, number> }> }).observations;
      if (observations.length > 0) {
        const wordCount = (text: string) => text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
        const totalWords = observations.reduce((sum, obs) => sum + wordCount(obs.description || ""), 0);
        const audioWordCount = (documentView?.audio_file?.metadata?.word_count as number | undefined) ?? null;
        const hasAudio = !!documentView?.audio_file?.url;

        return (
          <div className="space-y-8">
            {/* Word count stats */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {audioWordCount !== null && (
                <span>{audioWordCount} {t("documentDetail.wordsAudio", "words (audio)")}</span>
              )}
              <span>{totalWords} {t("documentDetail.wordsObservations", "words (observations)")}</span>
              <span>{observations.length} {t("documentDetail.observations", "observations")}</span>
            </div>

            {observations.map((observation, index) => {
              const photos = (observation.photo_ids || [])
                .map(id => ({ id, url: getPhotoUrl(id) }))
                .filter(p => p.url);
              const obsWords = wordCount(observation.description || "");
              const hasTimestamp = observation.start_time != null && observation.start_time >= 0;
              const isPlaying = playingObsIndex === index;
              const nextStart = observations[index + 1]?.start_time ?? null;
              const displayTime = isPlaying && playbackTime !== null ? playbackTime : observation.start_time ?? 0;

              return (
                <div key={index} className="flex flex-col gap-2 max-w-[550px]">
                  {/* Title row */}
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-semibold text-foreground">{observation.title}</h3>
                    <div className="flex items-center gap-3 shrink-0">
                      {hasTimestamp && hasAudio && (
                        <button
                          onClick={() => playObservation(index, observation.start_time!, nextStart)}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded transition-colors ${
                            isPlaying
                              ? "text-red-600 hover:text-red-800 dark:text-red-200 hover:bg-red-50 dark:hover:bg-red-950"
                              : "text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-950"
                          }`}
                          title={isPlaying ? "Stop" : `Play from ${formatTimestamp(observation.start_time!)}`}
                        >
                          {isPlaying ? (
                            <Square className="h-3 w-3 fill-current" />
                          ) : (
                            <Play className="h-3 w-3 fill-current" />
                          )}
                          <span className="font-mono">{formatTimestamp(displayTime)}</span>
                        </button>
                      )}
                      {hasTimestamp && !hasAudio && (
                        <span className="text-xs text-muted-foreground font-mono">{formatTimestamp(observation.start_time!)}</span>
                      )}
                      <span className="text-xs text-muted-foreground">{obsWords} {t("documentDetail.words", "words")}</span>
                    </div>
                  </div>
                  <p className="text-foreground whitespace-pre-wrap">{observation.description}</p>
                  {/* Raw transcript words - collapsed */}
                  {observation.raw_words && observation.raw_words.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
                        {t("documentDetail.rawTranscript", "Raw transcript")} ({observation.raw_words.length} {t("documentDetail.words", "words")})
                      </summary>
                      <div className="mt-1 max-h-48 overflow-y-auto rounded border border-border bg-muted p-2">
                        {observation.raw_words.map((w, wi) => (
                          <div key={wi} className="flex gap-2 text-xs leading-5 font-mono">
                            <span className="text-muted-foreground shrink-0 w-12 text-right">{formatTimestamp(w.start_sec)}</span>
                            <span className="text-muted-foreground">{w.word}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {/* Photos grid */}
                  {photos.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-1">
                      {photos.map((photo) => (
                        <div key={photo.id} className="relative">
                          <button
                            onClick={() => setZoomedImage({ url: photo.url!, title: observation.title, timestamp: getPhotoTimestamp(photo.id) })}
                            className="relative aspect-square w-full overflow-hidden rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                          >
                            <img
                              src={photo.url!}
                              alt={`${observation.title} - photo`}
                              className="w-full h-full object-cover"
                            />
                            {observation.photo_timestamps?.[photo.id] != null && (
                              <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5 font-mono">
                                {formatTimestamp(observation.photo_timestamps[photo.id])}
                              </span>
                            )}
                          </button>
                          {getPhotoMetadata(photo.id)?.nara && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setPhotoMetadata(getPhotoMetadata(photo.id)); }}
                              className="absolute top-1 right-1 p-0.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                              title="Photo info"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }
    }

    // Handle transcript segments format
    if (
      typeof content === "object" &&
      content !== null &&
      "segments" in content
    ) {
      const segments = (content as { segments: Array<{ id: string; text: string; speaker?: string }> }).segments;
      return (
        <div className="space-y-3">
          {segments.map((segment, index) => (
            <div key={segment.id || index} className="flex gap-3">
              {segment.speaker && (
                <span className="text-sm font-medium text-blue-600 w-20 shrink-0">
                  {segment.speaker}
                </span>
              )}
              <p className="text-foreground">{segment.text}</p>
            </div>
          ))}
        </div>
      );
    }

    // Handle markdown content
    if (documentView?.content_md) {
      return (
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <MarkdownWithImages content={documentView.content_md} imageFiles={documentView?.image_files as any} />
        </div>
      );
    }

    // Empty markdown document — prompt to edit
    if (isMarkdownDocument()) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">{t("documentDetail.emptyMarkdown", "This document is empty.")}</p>
          {urls && projectId && documentId && (
            <button
              onClick={() => navigate(urls.documentMarkdownEdit(projectId, documentId))}
              className="kii-btn kii-btn-soft kii-btn-sm"
            >
              <Pencil className="h-4 w-4" />
              {t("documentDetail.editMarkdown", "Edit Markdown")}
            </button>
          )}
        </div>
      );
    }

    // Handle plain text content_json
    if (typeof content === "string") {
      return (
        <div className="whitespace-pre-wrap text-foreground">{content}</div>
      );
    }

    // No content available
    if (!content) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          {t("documentDetail.noContent")}
        </div>
      );
    }

    // Handle other JSON (fallback)
    return (
      <pre className="text-sm text-foreground overflow-x-auto">
        {JSON.stringify(content, null, 2)}
      </pre>
    );
  };

  // Show loading skeleton while workspace or document is loading
  if (workspaceLoading || documentLoading || viewLoading) {
    return (
      <div>
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2 mb-2">
          <div className="h-4 w-16 bg-muted rounded animate-pulse" />
          <div className="h-4 w-4 bg-muted rounded animate-pulse" />
          <div className="h-4 w-24 bg-muted rounded animate-pulse" />
          <div className="h-4 w-4 bg-muted rounded animate-pulse" />
          <div className="h-4 w-32 bg-muted rounded animate-pulse" />
        </div>
        {/* Title skeleton */}
        <div className="h-8 w-64 bg-muted rounded animate-pulse mb-6" />
        {/* Status bar skeleton */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-6 w-20 bg-muted rounded-full animate-pulse" />
          <div className="h-6 w-24 bg-muted rounded-full animate-pulse" />
          <div className="h-6 w-32 bg-muted rounded-full animate-pulse" />
        </div>
        {/* Content skeleton */}
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <div className="h-5 w-3/4 bg-muted rounded animate-pulse" />
          <div className="h-5 w-full bg-muted rounded animate-pulse" />
          <div className="h-5 w-5/6 bg-muted rounded animate-pulse" />
          <div className="h-5 w-2/3 bg-muted rounded animate-pulse" />
          <div className="h-40 w-full bg-muted rounded animate-pulse mt-4" />
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium text-foreground mb-2">
          {t("documentDetail.notFound")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t("documentDetail.notFoundDescription")}
        </p>
        {urls && (
          <Link
            to={projectId ? urls.project(projectId) : urls.projects}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t("documentDetail.backToProject")}
          </Link>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={document.name}
        onTitleSave={(newName) => {
          if (!documentId || !projectId) return;
          updateName.mutate(
            { documentId, name: newName, projectId },
            {
              onSuccess: () => setActionError(null),
              onError: (error) => {
                console.error("Failed to rename document:", error);
                setActionError(t("documentDetail.saveError"));
              },
            }
          );
        }}
        breadcrumbs={[
          { label: "Projects", href: urls?.projects },
          {
            label: project?.name || "Project",
            href: urls && projectId ? urls.project(projectId) : undefined,
          },
          { label: document.name },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors text-sm"
                >
                  <X className="h-4 w-4" />
                  {t("documentDetail.cancel")}
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={updateContent.isPending}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  <Save className="h-4 w-4" />
                  {updateContent.isPending ? t("documentDetail.saving") : t("documentDetail.save")}
                </button>
              </>
            ) : (
              <>
                {hasObservationsFormat() && urls && projectId && documentId && (
                  <button
                    onClick={() => navigate(urls.documentTranscriptEdit(projectId, documentId))}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Pencil className="h-4 w-4" />
                    {t("documentDetail.edit")}
                  </button>
                )}
                {isMarkdownDocument() && urls && projectId && documentId && (
                  <button
                    onClick={() => navigate(urls.documentMarkdownEdit(projectId, documentId))}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Pencil className="h-4 w-4" />
                    {t("documentDetail.edit")}
                  </button>
                )}
                {!isMarkdownDocument() && !hasObservationsFormat() && getEffectiveStatus() === "ready" && (
                  <button
                    onClick={() => urls && projectId && documentId && navigate(urls.documentGenerate(projectId, documentId))}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <FileDown className="h-4 w-4" />
                    {t("documentDetail.generateDocument", "Generate Document")}
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                  <button
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                    aria-label="More actions"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {!isMarkdownDocument() && (
                    <DropdownMenuItem
                      onClick={() => setIsTranscribeDialogOpen(true)}
                      disabled={processDocument.isPending}
                      className="cursor-pointer"
                    >
                      {processDocument.isPending ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      {t("documentDetail.transcribe")}
                    </DropdownMenuItem>
                  )}
                  {hasObservationsFormat() ? (
                    <DropdownMenuItem
                      onClick={handleEditTranscript}
                      className="cursor-pointer"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      {t("documentDetail.edit")}
                    </DropdownMenuItem>
                  ) : !isMarkdownDocument() && (
                    <DropdownMenuItem
                      onClick={handleStartEdit}
                      className="cursor-pointer"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {t("documentDetail.edit")}
                    </DropdownMenuItem>
                  )}
                  {getEffectiveStatus() === "processing" && (
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          if (documentId && projectId) {
                            markReady.mutate({ documentId, projectId }, {
                              onError: (error) => {
                                console.error("Failed to mark document ready:", error);
                                setActionError(t("documentDetail.markReadyError"));
                              },
                            });
                          }
                        }}
                        disabled={markReady.isPending}
                        className="cursor-pointer"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t("documentDetail.markAsReady", "Mark as Ready")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {!isMarkdownDocument() && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setIsDeleteTranscriptionDialogOpen(true)}
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                      >
                        <Eraser className="h-4 w-4 mr-2" />
                        {t("documentDetail.deleteTranscription")}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t("documentDetail.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        }
      />

      {actionError && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {actionError}
        </p>
      )}

      {/* Document Info */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-4 lg:mb-6 text-sm text-muted-foreground">
        {document?.document_type && (
          <KiiBadge variant="secondary">
            {document.document_type}
          </KiiBadge>
        )}
        {getStatusBadge(getEffectiveStatus())}
        <ProductExtractionStatus documentId={documentId} />
        {(() => {
          const content = documentView?.content_json as Record<string, unknown> | undefined;
          const recordingStartedAt = content?.recording_started_at as string | undefined;
          if (recordingStartedAt) {
            return (
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatDateHook(recordingStartedAt, "long-datetime")}
              </span>
            );
          }
          return (
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">{t("documentDetail.updated")}</span> {formatDateHook(document.updated_at, "long-datetime")}
            </span>
          );
        })()}
        {(() => {
          const fileCount = (documentView?.image_files?.length || 0)
            + (documentView?.document_files?.length || 0)
            + (documentView?.audio_file ? 1 : 0);
          return fileCount > 0 ? (
            <span className="flex items-center gap-1">
              <Paperclip className="h-4 w-4" />
              {fileCount} {fileCount === 1 ? t("documentDetail.file", "file") : t("documentDetail.files", "files")}
            </span>
          ) : null;
        })()}
      </div>

      {/* Outside the banner: the Transcribe menu starts the same job on a document with no banner */}
      {processDocument.error instanceof InsufficientBoostsError && (
        <div className="mb-4 lg:mb-6"><BoostGateError error={processDocument.error} /></div>
      )}
      {processDocument.isError && !(processDocument.error instanceof InsufficientBoostsError) && (
        <p role="alert" className="mb-4 lg:mb-6 text-sm text-destructive">
          {t("documentDetail.processError")}
        </p>
      )}

      {/* Failure / Stuck banner */}
      {(document?.status === "failed" || (document?.status === "processing" && isStuck())) && (
        <div className={`rounded-lg border px-4 py-3 mb-4 lg:mb-6 ${
          document?.status === "failed"
            ? "border-destructive/50 bg-destructive/10 text-destructive dark:border-destructive"
            : "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/50"
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="flex-1 min-w-0">
              {document?.status === "failed" && document?.failure_reason && (
                <p className="text-sm">
                  <span className="font-medium">{t("documentDetail.failureReason", "Failure reason")}:</span>{" "}
                  {document.failure_reason}
                </p>
              )}
              {document?.status === "processing" && isStuck() && (
                <p className={`text-sm font-medium text-amber-800 dark:text-amber-200`}>
                  {t("documentDetail.statusStuck", "Stuck — retry?")}
                </p>
              )}
            </div>
            <button
              onClick={handleProcess}
              disabled={processDocument.isPending}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors shrink-0 ${
                document?.status === "failed"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                  : "bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              }`}
            >
              {processDocument.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t("documentDetail.retrying", "Retrying...")}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  {t("documentDetail.retry", "Retry")}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Audio File (NaraMachine format) */}
      {documentView?.audio_file?.url && (
        <div className="mb-4 lg:mb-6">
          <h3 className="text-sm font-medium text-foreground mb-2">{t("documentDetail.audio")}</h3>
          {(() => {
            const content = documentView?.content_json as Record<string, unknown> | undefined;
            const recordingStartedAt = content?.recording_started_at as string | undefined;
            if (recordingStartedAt) {
              const start = new Date(recordingStartedAt);
              const end = audioDuration ? new Date(start.getTime() + audioDuration * 1000) : null;
              return (
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Clock className="h-3.5 w-3.5" />
                  <span>
                    {formatDateHook(recordingStartedAt, "long-datetime")}
                    {end ? ` \u2013 ${formatDateHook(end, "time")}` : ""}
                  </span>
                </div>
              );
            }
            return null;
          })()}
          <audio
            ref={audioRef}
            controls
            className="w-full max-w-full"
            preload="metadata"
            onLoadedMetadata={(e) => setAudioDuration((e.target as HTMLAudioElement).duration)}
          >
            <source src={documentView.audio_file.url} type={documentView.audio_file.mime_type || 'audio/mpeg'} />
            Your browser does not support the audio element.
          </audio>
        </div>
      )}

      {/* Image Files (NaraMachine format) - only show if NOT observations format */}
      {documentView?.image_files && documentView.image_files.filter((f: any) => f.url).length > 0 && !hasObservationsFormat() && !isMarkdownDocument() && (
        <div className="mb-4 lg:mb-6">
          <h3 className="text-sm font-medium text-foreground mb-2">
            {t("documentDetail.photos", { count: documentView.image_files.filter((f: any) => f.url).length })}
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 lg:gap-3">
            {documentView.image_files.filter((f: any) => f.url).map((file: any) => (
              <div key={file.id} className="relative">
                <button
                  onClick={() => setZoomedImage({ url: file.url, title: file.filename || file.name || 'Photo', timestamp: file.metadata?.nara?.created_at as string | undefined })}
                  className="aspect-square w-full overflow-hidden rounded-lg border hover:opacity-90 transition-opacity cursor-pointer"
                >
                  <img
                    src={file.url}
                    alt={file.filename || file.name || 'Photo'}
                    className="w-full h-full object-cover"
                  />
                </button>
                {file.metadata?.nara && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setPhotoMetadata(file.metadata); }}
                    className="absolute top-1 right-1 p-0.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                    title="Photo info"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Files (NaraMachine format - PDFs, etc.) */}
      {documentView?.document_files && documentView.document_files.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-foreground mb-2">{t("documentDetail.documents")}</h3>
          <div className="flex flex-wrap gap-2">
            {documentView.document_files.map((file) => (
              <a
                key={file.id}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-accent rounded-lg text-sm text-foreground transition-colors"
              >
                <Download className="h-4 w-4" />
                {file.filename || file.name || 'Document'}
              </a>
            ))}
          </div>
        </div>
      )}

      <GeneratedReportLinks projectId={projectId} documentId={documentId} />

      {/* Legacy Files format */}
      {documentView?.files && documentView.files.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-foreground mb-2">{t("documentDetail.files")}</h3>
          <div className="flex flex-wrap gap-2">
            {documentView.files.map((file) => (
              <a
                key={file.id}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-accent rounded-lg text-sm text-foreground transition-colors"
              >
                <Download className="h-4 w-4" />
                {file.filename || file.name}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-card rounded-lg border border-border p-4 lg:p-6">
        {isEditing ? (
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full h-64 sm:h-80 lg:h-96 p-3 lg:p-4 font-mono text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            placeholder="Enter document content..."
          />
        ) : (
          renderContent()
        )}
      </div>

      {/* Edit History */}
      {projectId && documentId && (
        <div className="mt-4 lg:mt-6">
          <DocumentHistory projectId={projectId} documentId={documentId} />
        </div>
      )}

      <KiiConfirmDialog
        open={isDeleteTranscriptionDialogOpen}
        onOpenChange={setIsDeleteTranscriptionDialogOpen}
        title={t("documentDetail.deleteTranscriptionTitle")}
        description={t("documentDetail.deleteTranscriptionDescription")}
        confirmText={t("documentDetail.deleteTranscriptionButton")}
        onConfirm={handleDeleteTranscription}
        variant="destructive"
      />

      <KiiConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDelete}
        title={t("documentDetail.deleteTitle")}
        description={t("documentDetail.deleteDescription")}
        confirmText={t("documentDetail.deleteButton")}
        cancelText={t("documentDetail.cancel")}
        variant="destructive"
      />

      <KiiConfirmDialog
        open={isTranscribeDialogOpen}
        onOpenChange={setIsTranscribeDialogOpen}
        onConfirm={handleProcess}
        title={t("documentDetail.transcribeTitle")}
        description={t("documentDetail.transcribeDescription")}
        confirmText={t("documentDetail.transcribeButton")}
        cancelText={t("documentDetail.cancel")}
      />

      {/* Image Zoom Dialog - shared by both observations photos and image_files grid */}
      <Dialog open={!!zoomedImage} onOpenChange={(open) => !open && setZoomedImage(null)}>
        <DialogContent className="max-w-2xl p-2">
          <DialogHeader className="sr-only">
            <DialogTitle>{zoomedImage?.title || "Photo"}</DialogTitle>
          </DialogHeader>
          <div className="w-full overflow-hidden rounded-md">
            <img
              src={zoomedImage?.url}
              alt={zoomedImage?.title}
              className="w-full h-auto object-contain"
            />
          </div>
          {zoomedImage?.timestamp && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground px-1 pt-1">
              <Clock className="h-3 w-3" />
              {formatDateHook(zoomedImage.timestamp, "long-datetime")}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Metadata Dialog */}
      <Dialog open={!!photoMetadata} onOpenChange={(open) => !open && setPhotoMetadata(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              Photo Info
            </DialogTitle>
          </DialogHeader>
          {photoMetadata?.nara && (() => {
            const nara = photoMetadata.nara;
            return (
              <div className="space-y-3 text-sm">
                {nara.created_at && (
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">Timestamp</div>
                      <div>{formatDateHook(nara.created_at, "long-datetime")}</div>
                    </div>
                  </div>
                )}
                {(nara.latitude != null || nara.longitude != null) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">GPS</div>
                      <div>{nara.latitude?.toFixed(6)}, {nara.longitude?.toFixed(6)}</div>
                      {nara.altitude != null && (
                        <div className="text-muted-foreground text-xs">{nara.altitude.toFixed(1)} m altitude</div>
                      )}
                    </div>
                  </div>
                )}
                {(nara.width != null || nara.height != null) && (
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">Dimensions</div>
                      <div>{nara.width} x {nara.height}{nara.format ? ` (${nara.format})` : ''}</div>
                    </div>
                  </div>
                )}
                {(nara.compass_heading != null || nara.pitch_degrees != null || nara.roll_degrees != null || nara.yaw_degrees != null) && (
                  <div className="flex items-start gap-2">
                    <Compass className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <div className="text-muted-foreground text-xs">Orientation</div>
                      {nara.compass_heading != null && <div>Heading: {nara.compass_heading.toFixed(1)}&deg;</div>}
                      {nara.pitch_degrees != null && <div>Pitch: {nara.pitch_degrees.toFixed(1)}&deg;</div>}
                      {nara.roll_degrees != null && <div>Roll: {nara.roll_degrees.toFixed(1)}&deg;</div>}
                      {nara.yaw_degrees != null && <div>Yaw: {nara.yaw_degrees.toFixed(1)}&deg;</div>}
                    </div>
                  </div>
                )}
                {(nara.pitch != null || nara.roll != null || nara.yaw != null) && (
                  <details className="mt-1">
                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
                      Raw radians
                    </summary>
                    <div className="mt-1 text-xs text-muted-foreground space-y-0.5 pl-6">
                      {nara.pitch != null && <div>Pitch: {nara.pitch.toFixed(4)} rad</div>}
                      {nara.roll != null && <div>Roll: {nara.roll.toFixed(4)} rad</div>}
                      {nara.yaw != null && <div>Yaw: {nara.yaw.toFixed(4)} rad</div>}
                    </div>
                  </details>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
